import { useGetActionBuilderActionsQuery } from 'src/services/action-builder/actionBuilderApi';

/**
 * Resolve an action's *current* display name from its stable action id.
 *
 * Execution records (preview logs, archived transcripts) store the action name
 * as it was when the tool ran, so a later rename leaves them showing a stale
 * label. The server threads the stable action id alongside that stored name;
 * this hook trades the id for the live name and falls back to the stored one
 * for older records that predate id propagation (or actions since deleted).
 *
 * `shouldReturnAll: true` on purpose: the `product` param filters the response
 * by action *type* enabled-for-product, not by ownership, and these logs render
 * in preview consoles (widget, email, voice) that do not all have a meaningful
 * product in scope. Returning every action keeps resolution from silently
 * missing. Note this is a different RTK Query cache key than the `{ product }`
 * query the autoflow editor uses, so both may be in flight on the workflow
 * builder page — see `ActionNodeDisplay`.
 */
export const useResolvedActionName = <T extends string | undefined>(
  actionId: string | null | undefined,
  storedActionName: T,
): string | T => {
  const { data: actionsData } = useGetActionBuilderActionsQuery(
    { shouldReturnAll: true },
    { skip: !actionId },
  );

  if (!actionId) {
    return storedActionName;
  }

  return (
    actionsData?.actions.find(action => action.action_id === actionId)
      ?.action_name || storedActionName
  );
};
